import React, { useEffect, useRef, useState } from 'react';
import { getQuestions } from '../services/chatbotApi';

// Input box with a type-to-filter suggestion dropdown of predefined questions.
export default function ChatInput({ onSelectQuestion, onFreeText, disabled }) {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced auto-filter as the user types.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.trim();
      if (!q) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      try {
        const results = q.length >= 3 ? await getQuestions({ keyword: q, limit: 8 }) : [];
        setSuggestions(results);
        setOpen(results.length > 0);
        setActiveIndex(-1);
      } catch (e) {
        setSuggestions([]);
        setOpen(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  // Close dropdown on outside click.
  useEffect(() => {
    function handleClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const pick = (q) => {
    if (!q) return;
    onSelectQuestion(q);
    setValue('');
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'Enter' && value.trim()) {
        e.preventDefault();
        onFreeText(value.trim());
        setValue('');
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(activeIndex >= 0 ? suggestions[activeIndex] : suggestions[0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="chatinput" ref={boxRef}>
      {open && suggestions.length > 0 && (
        <ul className="suggest" role="listbox">
          <li className="suggest__hint">
            {suggestions.length} matching question{suggestions.length > 1 ? 's' : ''}
          </li>
          {suggestions.map((s, i) => (
            <li
              key={s.id}
              role="option"
              aria-selected={i === activeIndex}
              className={`suggest__item ${i === activeIndex ? 'is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
            >
              <span className="suggest__q">{s.displayQuestion}</span>
              <span className="suggest__cat">{s.categoryName || 'Question'}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="chatinput__bar">
        <input
          type="text"
          className="chatinput__field"
          placeholder="Ask about a molecule, patent, approval… (e.g. Chlorantraniliprole)"
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          aria-label="Ask a question"
          autoComplete="off"
        />
        <button
          type="button"
          className="chatinput__send"
          disabled={disabled || (!value.trim() && suggestions.length === 0)}
          onClick={() => suggestions.length ? pick(activeIndex >= 0 ? suggestions[activeIndex] : suggestions[0]) : onFreeText(value.trim())}
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path fill="currentColor" d="M3 11.5L21 3l-8.5 18-2.2-7.3L3 11.5z" />
          </svg>
        </button>
      </div>
      <p className="chatinput__note">
        Answers are limited to the ChemRobotics knowledge base.
      </p>
    </div>
  );
}
